import { css } from '@emotion/react';
import { DeviceType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config.pb';
import { useTranslation } from '@frontend/i18n';
import { CallGroups } from '@frontend/phones#svg';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Text, Table, Chip } from '@frontend/design-system';
import { getNameWithoutExtension } from '../../../../utils/phone-utils';
import { CachedAudioScrubber } from '../../../common/cached-audio-scrubber';

export enum DeviceType {
  Desk = 'desk',
  Softphone = 'softphone',
  Mobile = 'mobile',
  ForwardingNumber = 'forwardingNumber',
  Unknown = 'unknown',
}
export type PreviewDeviceObject = {
  name: string;
  type: DeviceType | DeviceType_Enum;
  locationIds?: string[];
  deviceId?: string;
  forwardingNumberId?: string;
};

type DevicesPreviewProps = {
  callObjectName: string;
  isLoading: boolean;
  devices: PreviewDeviceObject[];
  showInitialState: boolean;
  editButton?: React.ReactNode;
  greetingData?: {
    mediaItemId: string;
    greetingName: string;
    mediaPath: string;
  };
  locationResolver?: {
    getDeviceLocation: (deviceId: string) => string;
    getForwardingNumberLocation: (forwardingNumberId: string) => string;
  };
};

export const DevicesPreview = ({
  devices,
  isLoading,
  showInitialState,
  greetingData,
  callObjectName,
  editButton,
  locationResolver,
}: DevicesPreviewProps) => {
  const { t } = useTranslation('phone');
  const { getScopeName, isSingleTypeScope } = useAppScopeStore();

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(1)};
      `}
    >
      <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${theme.spacing(1)};
          `}
        >
          <Text weight='bold' size='large'>
            {t('Preview {{callObjectName}}', {
              callObjectName,
            })}
          </Text>
          <Text color='subdued' size='large'>
            {t('{{count}} Devices', {
              count: devices.length,
            })}
          </Text>
        </div>
        {editButton}
      </div>
      <div
        css={css`
          width: 100%;
          box-shadow: ${theme.shadows.light};
          border-radius: ${theme.borderRadius.medium};
          padding: ${theme.spacing(2)};
          gap: ${theme.spacing(2)};
          display: flex;
          flex-direction: column;
        `}
      >
        {showInitialState && (
          <div
            css={css`
              padding: ${theme.spacing(5)};
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            `}
          >
            <CallGroups width={336} height={336} />
            <Text color='subdued' size='large'>
              {t('Select a {{callObjectName}}', {
                callObjectName,
              })}
            </Text>
          </div>
        )}

        {!showInitialState && (
          <>
            {!!greetingData && (
              <div
                css={css`
                  display: flex;
                  gap: ${theme.spacing(2)};
                `}
              >
                <div>
                  <Text color='subdued' size='small'>
                    {t('Greeting')}
                  </Text>
                  <Text size='medium'>{getNameWithoutExtension(greetingData.greetingName) || '--'}</Text>
                </div>
                {greetingData.mediaItemId && (
                  <CachedAudioScrubber
                    customWidth={205}
                    singlePlayer
                    filePath={greetingData.mediaPath}
                    mediaId={greetingData.mediaItemId}
                  />
                )}
              </div>
            )}

            <Table
              data={devices}
              emptyStateConfig={{
                description: t('No assigned devices'),
                header: '',
                type: 'phone_data',
              }}
              isLoading={isLoading}
              fullHeight
              fullHeightConfig={{
                maxHeight: 400,
                minHeight: 200,
              }}
              colConfig={[
                {
                  Header: t('Assigned Device (Type)'),
                  accessor: (device) => device,
                  id: 'device',
                  cellRenderer: (device: PreviewDeviceObject) => {
                    let type = '';

                    if (device.type === 'desk') {
                      type = t('desk');
                    } else if (device.type === 'softphone') {
                      type = t('softphone');
                    } else if (device.type === 'mobile') {
                      type = t('mobile');
                    } else if (device.type === 'forwardingNumber') {
                      type = t('forwarding number');
                    }

                    return `${device.name} (${type})`;
                  },
                },
                {
                  id: 'location',
                  Header: t('Locations'),
                  accessor: (data) => {
                    // Use locationResolver for call groups
                    if (locationResolver && (data.deviceId || data.forwardingNumberId)) {
                      let locationName = '';
                      if (data.forwardingNumberId) {
                        locationName = locationResolver.getForwardingNumberLocation(data.forwardingNumberId);
                      } else if (data.deviceId) {
                        locationName = locationResolver.getDeviceLocation(data.deviceId);
                      }

                      if (!locationName || locationName.trim() === '') {
                        return '--';
                      }

                      return <Chip.SingleChip>{locationName}</Chip.SingleChip>;
                    }

                    // Use locationIds for call queues
                    if (!data.locationIds || data.locationIds.length === 0) {
                      return '--';
                    }
                    const validLocationNames = data.locationIds
                      .map((locationId) => getScopeName(locationId))
                      .filter((name) => name && name.trim() !== '');

                    if (validLocationNames.length === 0) {
                      return '--';
                    }

                    const locationNames = validLocationNames.join(', ');
                    return <Chip.SingleChip>{locationNames}</Chip.SingleChip>;
                  },
                  omit: isSingleTypeScope,
                },
              ]}
            />
          </>
        )}
      </div>
    </div>
  );
};
